import { Column, Index } from 'typeorm';
import type { AssociationMemberSource, AssociationMemberStatus } from '../../constants/associations';
import { Entity } from '../../decorators';
import type { ImportRowUndoModel } from '../import/import-run.models';
import { BaseEntity } from './base.entity';

/**
 * PD-17639, PD-17641: a real account or competitor that belongs to an association.
 * Stays in the tenant: a member-only list is the tenant's alone, and the Workd-wide
 * directory holds public member directories only.
 */
@Entity('association_member_link')
@Index('idx_association_member_association', ['directoryAssociationId', 'status'])
@Index('idx_association_member_client', ['clientId'])
@Index('idx_association_member_run', ['importRunId'])
export class AssociationMemberLinkEntity extends BaseEntity {
	@Column('int', { name: 'directoryAssociationId' })
	public directoryAssociationId!: number;

	@Column('int', { name: 'fk_client__id', unsigned: true, nullable: true })
	public clientId?: number | null;

	/** Set for a competitor, which has no account. */
	@Column('int', { name: 'fk_wholesaler__id', unsigned: true, nullable: true })
	public wholesalerId?: number | null;

	/** The person named on a member list, when there was one. */
	@Column('int', { name: 'fk_contact__id', unsigned: true, nullable: true })
	public contactId?: number | null;

	@Column('varchar', { name: 'memberType', length: 60, nullable: true })
	public memberType?: string | null;

	@Column('varchar', { name: 'status', length: 20, default: 'current' })
	public status!: AssociationMemberStatus;

	@Column('int', { name: 'sinceYear', nullable: true })
	public sinceYear?: number | null;

	@Column('int', { name: 'lastYear', nullable: true })
	public lastYear?: number | null;

	@Column('varchar', { name: 'source', length: 20 })
	public source!: AssociationMemberSource;

	@Column('text', { name: 'note', nullable: true })
	public note?: string | null;

	/** The import that brought this row in, so the import can be undone. */
	@Column('int', { name: 'fk_import_run__id', nullable: true })
	public importRunId?: number | null;

	/** This row's import created the account, so undoing the import removes it. */
	@Column('boolean', { name: 'createdClient', default: 0 })
	public createdClient!: boolean;

	/** What this row wrote on an account already in Workd, to put back on undo. */
	@Column('json', { name: 'undoData', nullable: true })
	public undoData?: ImportRowUndoModel | null;
}
